import React, { FC, ReactNode, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { animateScroll } from 'react-scroll';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import { IndexAreaLoadingSpinner } from '@buyside/components/presentation/IndexPage/IndexPage';

import {
  HomePageCommentsQuery,
  HomePageIdeaUpdatesQuery,
  MainNavigationCurrentUserQuery,
  useHomePageIdeaUpdatesLazyQuery,
  useHomePageCommentsLazyQuery,
  useMainNavigationCurrentUserQuery
} from '@interfaces/graphql-typings';

export enum DataType {
  IDEA_UPDATES = 'Idea Updates',
  COMMENTS = 'Comments'
}

interface SharedChildrenProps {
  loading: boolean;
  userData?: MainNavigationCurrentUserQuery;
}

interface IdeaUpdateProps {
  children: (props: SharedChildrenProps & { data?: HomePageIdeaUpdatesQuery }) => ReactNode;
  type: DataType.IDEA_UPDATES;
  useLazyQuery: typeof useHomePageIdeaUpdatesLazyQuery;
}

interface Commentprops {
  children: (props: SharedChildrenProps & { data?: HomePageCommentsQuery }) => ReactNode;
  type: DataType.COMMENTS;
  useLazyQuery: typeof useHomePageCommentsLazyQuery;
}

import styles from '@buyside/components/presentation/HomePage/homePageStyles.scss';

const HomePagePanel: FC<(IdeaUpdateProps | Commentprops) & {
  icon: SVGAElement;
}> = ({ children, useLazyQuery }) => {
  const [searchParams] = useSearchParams();

  const { data: userData } = useMainNavigationCurrentUserQuery();

  const [loadData, { data, loading, variables, previousData }] = useLazyQuery();

  const currentOffset = variables?.offset || 0;

  const fetchAndMaybeScroll = useCallback(
    (offset: number, params: URLSearchParams, skipScroll?: boolean) => {
      loadData({ variables: { offset, searchParams: params.toString() } }).then(() => {
        if (skipScroll) return;
        animateScroll.scrollToTop();
      });
    },
    [loadData]
  );

  useEffect(() => {
    fetchAndMaybeScroll(0, searchParams, true);
  }, [fetchAndMaybeScroll, searchParams]);

  return (
    <main className={styles.panelWrapper}>
      <IndexAreaLoadingSpinner loading={loading} data={data || previousData}>
        {/* @ts-ignore - data: 'Type <Union (|)> is not assignable to type <Intersection (&)>' */}
        {children({ loading, data: data || previousData, userData })}
      </IndexAreaLoadingSpinner>
      <div className={styles.buttons}>
        <Button
          id="prev-button"
          disabled={currentOffset === 0}
          isLoading={loading}
          onClick={(): void => fetchAndMaybeScroll(currentOffset - 1, searchParams)}
          size={BUTTON_SIZES.SMALL}
          text="< Previous"
          type={BUTTON_TYPES.LINK}
        />
        <Button
          id="next-button"
          disabled={currentOffset === data?.maxOffset}
          isLoading={loading}
          onClick={(): void => fetchAndMaybeScroll(currentOffset + 1, searchParams)}
          size={BUTTON_SIZES.SMALL}
          text="Next >"
          type={BUTTON_TYPES.LINK}
        />
      </div>
    </main>
  );
};

export default HomePagePanel;
